import { BookOpenText, Drama, Settings as SettingsIcon, Sun } from 'lucide-react';
import { lazy, Suspense, useEffect } from 'react';
import { IllustrationDefs } from '../components/Illustration';
import { ToastHost } from '../components/Toast';
import { About } from '../screens/About';
import { Onboarding } from '../screens/Onboarding';
import { PhraseDetail } from '../screens/PhraseDetail';
import { Phrases } from '../screens/Phrases';
import { ReadingGuide } from '../screens/ReadingGuide';
import { Settings } from '../screens/Settings';
import { Stories } from '../screens/Stories';
import { Today } from '../screens/Today';
import { Session } from '../session/Session';
import { useAppState } from '../state/store';
import { href, useRoute, type Route } from './router';

// The story player brings the animation engine with it, so it loads only when a story opens.
const StoryPlayer = lazy(() => import('../stories/player/StoryPlayer'));

const TITLES: Record<Route['name'], string> = {
  today: 'Today',
  session: 'Practice',
  phrases: 'Phrasebook',
  phrase: 'Phrase',
  stories: 'Stories',
  story: 'Story',
  settings: 'Settings',
  reading: 'Reading guide',
  about: 'How this works',
};

function TabBar({ route }: { route: Route }) {
  const current = route.name === 'phrase' ? 'phrases' : route.name === 'story' ? 'stories' : route.name === 'reading' ? 'today' : route.name === 'about' ? 'settings' : route.name;
  const tabs = [
    { name: 'today', path: '/', label: 'Today', icon: Sun },
    { name: 'phrases', path: '/phrases', label: 'Phrases', icon: BookOpenText },
    { name: 'stories', path: '/stories', label: 'Stories', icon: Drama },
    { name: 'settings', path: '/settings', label: 'Settings', icon: SettingsIcon },
  ] as const;
  return (
    <nav className="tabbar" aria-label="Main">
      <a className="brand" href={href('/')}>
        <span className="brand-mark" aria-hidden="true">
          α
        </span>
        Learning Greek
      </a>
      {tabs.map(({ name, path, label, icon: Icon }) => (
        <a key={name} className="tab" href={href(path)} aria-current={current === name ? 'page' : undefined}>
          <Icon size={22} aria-hidden="true" />
          {label}
        </a>
      ))}
    </nav>
  );
}

export function App() {
  const state = useAppState();
  const route = useRoute();
  const theme = state.settings.theme;

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#141517' : '#f6f2ea');
  }, [theme]);

  useEffect(() => {
    document.title = route.name === 'today' ? 'Learning Greek' : `${TITLES[route.name]} · Learning Greek`;
  }, [route.name]);

  // A new screen starts at the top (a lesson link scrolls to its section instead).
  useEffect(() => {
    if (!(route.name === 'phrases' && route.lesson)) window.scrollTo({ top: 0 });
  }, [route.name, state.profile.onboarded]); // eslint-disable-line react-hooks/exhaustive-deps

  // The toast host sits outside the screens, so a message survives moving between them.
  return (
    <div className="app">
      <IllustrationDefs />
      <Screen route={route} onboarded={state.profile.onboarded} />
      <ToastHost />
    </div>
  );
}

function Screen({ route, onboarded }: { route: Route; onboarded: boolean }) {
  if (!onboarded) return <Onboarding />;
  if (route.name === 'session') return <Session />;
  if (route.name === 'story')
    return (
      <Suspense fallback={<div className="story-page" aria-busy="true" />}>
        <StoryPlayer key={route.id} id={route.id} />
      </Suspense>
    );

  let screen: React.ReactNode;
  switch (route.name) {
    case 'phrases':
      screen = <Phrases lesson={route.lesson} />;
      break;
    case 'phrase':
      screen = <PhraseDetail id={route.id} />;
      break;
    case 'stories':
      screen = <Stories />;
      break;
    case 'settings':
      screen = <Settings />;
      break;
    case 'reading':
      screen = <ReadingGuide />;
      break;
    case 'about':
      screen = <About />;
      break;
    default:
      screen = <Today />;
  }

  return (
    <>
      <a href="#main" className="sr-only" onClick={(e) => { e.preventDefault(); const main = document.getElementById('main'); main?.setAttribute('tabindex', '-1'); main?.focus(); }}>
        Skip to content
      </a>
      {screen}
      <TabBar route={route} />
    </>
  );
}
